import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../../utils/api";
import { toast } from "react-toastify";
import { formatDate } from "../../utils/helpers";
import AdminNav from "../../components/AdminNav";
import { normalizePaymentMethods } from "../../utils/paymentMethods";
import { normalizeShippingMethods } from "../../utils/shippingMethods";

const Users = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialTab = searchParams.get("tab") === "all" ? "all" : "pending";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectingUserId, setRejectingUserId] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [shippingMethods, setShippingMethods] = useState([]);
  const [pendingPaymentSelections, setPendingPaymentSelections] = useState({});

  useEffect(() => {
    const tab = searchParams.get("tab") === "all" ? "all" : "pending";
    setActiveTab(tab);
  }, [searchParams]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams(tab === "all" ? { tab: "all" } : {});
  };

  const fetchMethodSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/settings/site");
      setPaymentMethods(normalizePaymentMethods(data.settings?.payment_methods || []));
      setShippingMethods(normalizeShippingMethods(data.settings?.shipping_methods || []));
    } catch (error) {
      console.error("Error fetching method settings:", error);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const [pendingRes, allRes] = await Promise.all([api.get("/admin/users/pending"), api.get("/admin/users")]);
      // Filter out admin users from the lists
      const filteredPendingUsers = pendingRes.data.users.filter(user => user.role !== 'admin');
      setPendingUsers(filteredPendingUsers);
      setAllUsers(allRes.data.users.filter(user => user.role !== 'admin'));
    } catch (error) {
      toast.error("Failed to fetch users");
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchMethodSettings();
  }, [fetchMethodSettings]);

  useEffect(() => {
    if (paymentMethods.length === 0 || pendingUsers.length === 0) {
      return;
    }

    setPendingPaymentSelections((prev) => {
      const nextSelections = { ...prev };
      pendingUsers.forEach((user) => {
        if (!nextSelections[user.id] || nextSelections[user.id].length === 0) {
          nextSelections[user.id] = user.allowed_payment_method_ids?.length ? user.allowed_payment_method_ids : paymentMethods.map((method) => method.id);
        }
      });
      return nextSelections;
    });
  }, [paymentMethods, pendingUsers]);

  const handleApprove = async (userId) => {
    try {
      await api.put(`/admin/users/${userId}/approve`, {
        allowed_payment_method_ids: pendingPaymentSelections[userId] || paymentMethods.map((method) => method.id),
        allowed_shipping_method_ids: shippingMethods.map((method) => method.id),
      });
      toast.success("User approved successfully");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to approve user");
      console.error("Error approving user:", error);
    }
  };

  const handleReject = async (userId) => {
    if (!rejectionReason.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }

    try {
      await api.put(`/admin/users/${userId}/reject`, { reason: rejectionReason });
      toast.success("User rejected");
      setRejectingUserId(null);
      setRejectionReason("");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to reject user");
      console.error("Error rejecting user:", error);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      approved: "bg-green-500 text-black",
      pending: "bg-yellow-500 text-black",
      rejected: "bg-red-500 text-white",
    };
    return <span className={`badge ${styles[status] || "bg-gray-500 text-white"}`}>{status}</span>;
  };

  const handlePendingPaymentToggle = (userId, paymentMethodId) => {
    setPendingPaymentSelections((prev) => {
      const currentSelection = prev[userId] || paymentMethods.map((method) => method.id);
      return {
        ...prev,
        [userId]: currentSelection.includes(paymentMethodId)
          ? currentSelection.filter((id) => id !== paymentMethodId)
          : [...currentSelection, paymentMethodId],
      };
    });
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6 sm:py-8">
        <div className="text-center text-gold-500">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <AdminNav />
      <div className="container mx-auto px-4 py-6 sm:py-8">
        <h1 className="heading-metallic text-3xl mb-6">User Management</h1>

        {/* Tabs */}
        <div className="mb-6 flex overflow-x-auto border-b border-gray-700">
          <button onClick={() => handleTabChange("pending")} className={`pb-2 px-4 ${activeTab === "pending" ? "border-b-2 border-gold-500 text-gold-500" : "text-gray-400 hover:text-white"}`}>
            Pending Approvals ({pendingUsers.length})
          </button>
          <button onClick={() => handleTabChange("all")} className={`pb-2 px-4 ${activeTab === "all" ? "border-b-2 border-gold-500 text-gold-500" : "text-gray-400 hover:text-white"}`}>
            All Users ({allUsers.length})
          </button>
        </div>

        {/* Pending Users Tab */}
        {activeTab === "pending" && (
          <div className="space-y-4">
            {pendingUsers.length === 0 ? (
              <p className="text-gray-400">No pending approvals</p>
            ) : (
              pendingUsers.map((user) => (
                <motion.div key={user.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card">
                  <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <h3 className="text-lg font-semibold text-white">
                        {user.first_name} {user.last_name}
                      </h3>
                      <p className="text-gray-400 text-sm">{user.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-300">
                        <strong>Company:</strong> {user.company_name}
                      </p>
                      <p className="text-sm text-gray-300">
                        <strong>Phone:</strong> {user.phone}
                      </p>
                    </div>
                  </div>

                  <div className="mb-4">
                    <p className="text-sm text-gray-300">
                      <strong>Address:</strong> {user.address_line1}, {user.city}, {user.state}, {user.country}
                    </p>
                    {user.tax_id && (
                      <p className="text-sm text-gray-300">
                        <strong>Tax ID:</strong> {user.tax_id}
                      </p>
                    )}
                    <p className="text-sm text-gray-400 mt-2">Applied: {formatDate(user.created_at)}</p>
                  </div>

                  <div className="mb-4 rounded-lg border border-gray-700 bg-black/30 p-4">
                    <p className="mb-3 text-sm font-medium text-gray-300">Allowed Payment Methods On Approval</p>
                    {paymentMethods.length === 0 ? (
                      <p className="text-sm text-gray-400">Create payment methods in Site Settings first.</p>
                    ) : (
                      <div className="grid gap-3 lg:grid-cols-2">
                        {paymentMethods.map((method) => (
                          <label key={method.id} className="flex items-center gap-3 rounded border border-gray-700 bg-black/40 px-4 py-3 text-sm text-white">
                            <input
                              type="checkbox"
                              checked={(pendingPaymentSelections[user.id] || paymentMethods.map((paymentMethod) => paymentMethod.id)).includes(method.id)}
                              onChange={() => handlePendingPaymentToggle(user.id, method.id)}
                            />
                            <span>{method.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  {rejectingUserId === user.id ? (
                    <div className="space-y-3">
                      <textarea value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} placeholder="Reason for rejection..." className="input-field w-full h-20" />
                      <div className="flex gap-2">
                        <button onClick={() => handleReject(user.id)} className="btn-primary">
                          Confirm Rejection
                        </button>
                        <button
                          onClick={() => {
                            setRejectingUserId(null);
                            setRejectionReason("");
                          }}
                          className="btn-secondary"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-3 sm:flex-row">
                      <button onClick={() => handleApprove(user.id)} className="btn-primary" disabled={paymentMethods.length > 0 && (pendingPaymentSelections[user.id] || []).length === 0}>
                        Approve
                      </button>
                      <button onClick={() => setRejectingUserId(user.id)} className="bg-red-600 hover:bg-red-500 text-white px-6 py-2 rounded-md transition-colors">
                        Reject
                      </button>
                    </div>
                  )}
                </motion.div>
              ))
            )}
          </div>
        )}

        {/* All Users Tab */}
        {activeTab === "all" && (
          <div className="card overflow-x-auto">
            <table className="w-full min-w-[46rem]">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 px-4 text-gray-300">Name</th>
                  <th className="text-left py-3 px-4 text-gray-300">Email</th>
                  <th className="text-left py-3 px-4 text-gray-300">Company</th>
                  <th className="text-left py-3 px-4 text-gray-300">Role</th>
                  <th className="text-left py-3 px-4 text-gray-300">Status</th>
                  <th className="text-left py-3 px-4 text-gray-300">Registered</th>
                </tr>
              </thead>
              <tbody>
                {allUsers.map((user) => (
                  <tr 
                    key={user.id} 
                    onClick={() => navigate(`/admin/users/${user.id}`)}
                    className="border-b border-gray-800 hover:bg-black-lighter cursor-pointer transition-colors"
                  >
                    <td className="py-3 px-4 text-white">
                      {user.first_name} {user.last_name}
                    </td>
                    <td className="py-3 px-4 text-gray-300">{user.email}</td>
                    <td className="py-3 px-4 text-gray-300">{user.company_name}</td>
                    <td className="py-3 px-4">
                      <span className={`badge ${user.role === "admin" ? "badge-gold" : "badge-outline"}`}>{user.role}</span>
                    </td>
                    <td className="py-3 px-4">{getStatusBadge(user.status)}</td>
                    <td className="py-3 px-4 text-gray-400 text-sm">{formatDate(user.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Users;
